import { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Tag, Space, message, Tabs, Card, Select, Typography, Alert, Descriptions, Upload, Popconfirm } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, FilePdfOutlined, UploadOutlined, BuildOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { BPMService } from '../api/services/bpm.service';
import { UsersService } from '../api/services/users.service';
import { useAuth } from '../store/auth';
import ProcessDetailModal from '../components/ProcessDetailModal';

const { Title, Text } = Typography;

export default function BPMPage() {
  const { user } = useAuth();
  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Verification State
  const [verifyHash, setVerifyHash] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState('');

  // Constructor State
  const [constructorVisible, setConstructorVisible] = useState(false);
  const [form] = Form.useForm();
  const [stages, setStages] = useState([[]]);

  // Land Analysis State
  const [landAnalysisVisible, setLandAnalysisVisible] = useState(false);
  const [landForm] = Form.useForm();
  const [isGenerating, setIsGenerating] = useState(false);
  const [fileList, setFileList] = useState([]);

  // Process Detail State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [currentProcess, setCurrentProcess] = useState(null);

  const handleLandAnalysisGenerate = async (values) => {
    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('cadastral_numbers', values.cadastral_numbers.join(','));
      formData.append('description', values.description || '');
      formData.append('territorial_zone', values.territorial_zone || '');
      formData.append('address_fallback', values.address_fallback || '');
      formData.append('calculation_method', values.calculation_method);
      formData.append('cost', values.cost);
      formData.append('cost_type', values.cost_type);
      
      fileList.forEach(f => {
        if (f.originFileObj) {
          formData.append('files', f.originFileObj);
        }
      });
      
      const res = await BPMService.generateLandAnalysis(formData);
      
      // Auto-start the process right after generation
      await BPMService.startLandAnalysis({
        process_instance_id: res.data.process_instance_id,
        description: res.data.description,
        calculation_method: res.data.calculation_method,
        cost: res.data.cost,
        cost_type: res.data.cost_type
      });
      
      message.success('Процесс успешно запущен!');
      setLandAnalysisVisible(false);
      landForm.resetFields();
      setFileList([]);
      fetchAll();
    } catch (err) {
      message.error('Ошибка при запуске процесса: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsGenerating(false);
    }
  };

  const landUploadProps = {
    onRemove: (file) => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
    },
    beforeUpload: (file) => {
      setFileList(prev => [...prev, file]);
      return false; // Prevent auto upload
    },
    fileList,
  };

  const fetchAll = () => {
    BPMService.getInstances().then(r => setInstances(r.data)).catch(() => {});
    BPMService.getTemplates().then(r => setTemplates(r.data)).catch(() => {});
    UsersService.getUsers().then(r => setUsers(r.data)).catch(() => {});
  };
  
  useEffect(() => { fetchAll(); }, []);

  const handleVerify = async () => {
    if (!verifyHash.trim()) return;
    setVerifyResult(null);
    setVerifyError('');
    try {
      const res = await BPMService.verifySignature(verifyHash.trim());
      setVerifyResult(res.data);
    } catch (err) {
      setVerifyError('Подпись не найдена или недействительна');
    }
  };

  const handleDeleteProcess = async (id) => {
    try {
      await BPMService.deleteInstance(id);
      message.success('Процесс успешно удален');
      fetchAll();
    } catch (err) {
      message.error('Ошибка при удалении процесса');
    }
  };

  const handleDownloadPdf = async (process) => {
    try {
      const res = await BPMService.getInstanceSteps(process.id);
      const steps = res.data;
      
      // Create off-screen element for PDF rendering
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      container.style.width = '1120px';
      container.style.padding = '40px';
      container.style.backgroundColor = 'white';
      container.style.fontFamily = 'Arial, sans-serif';
      
      let htmlContent = `
        <div style="font-size: 10px; color: #888; margin-bottom: 20px;">
          ID Документа: ${process.target_file_id || 'Неизвестно'}
        </div>
        <h2 style="font-size: 20px; margin-bottom: 10px; color: #000;">
          Лист согласования к документу: ${process.target_file_name}
        </h2>
        <div style="font-size: 14px; margin-bottom: 30px; color: #000;">
          Связанная задача: ${process.title || 'Задача'} (ID: ${process.task_id})
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px; color: #000;">
          <thead>
            <tr style="border-bottom: 2px solid #000; text-align: left;">
              <th style="padding: 8px 0;">ФИО согласующего</th>
              <th style="padding: 8px 0;">Статус</th>
              <th style="padding: 8px 0;">Дата</th>
              <th style="padding: 8px 0;">ЭЦП (Хэш подписи)</th>
            </tr>
          </thead>
          <tbody>
      `;
      
      steps.forEach(step => {
        const approver = users.find(u => String(u.id) === String(step.approver_id));
        const name = approver ? approver.full_name : `Сотрудник ID: ${step.approver_id}`;
        let statusText = step.status === 'pending' ? 'Ожидает' : 
                         step.status === 'approved' ? 'Согласовано' : 'Отклонено';
        const dateStr = step.decided_at ? dayjs(step.decided_at).format('DD.MM.YYYY HH:mm') : '';
        const signStr = step.signature_hash ? step.signature_hash : '___________________ (Подпись)';
        const commentHtml = step.comment ? `<div style="font-size: 11px; color: #555; margin-top: 4px; white-space: pre-wrap; word-break: break-word;">Комментарий: ${step.comment}</div>` : '';
        
        htmlContent += `
            <tr style="border-bottom: 1px solid #ccc;">
              <td style="padding: 10px 0; width: 30%;">
                <div>${name}</div>
                ${commentHtml}
              </td>
              <td style="padding: 10px 0; width: 10%;">${statusText}</td>
              <td style="padding: 10px 0; width: 15%;">${dateStr}</td>
              <td style="padding: 10px 0; word-break: break-all; width: 45%;">${signStr}</td>
            </tr>
        `;
      });
      
      htmlContent += `
          </tbody>
        </table>
      `;
      
      container.innerHTML = htmlContent;
      document.body.appendChild(container);
      
      const canvas = await html2canvas(container, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      document.body.removeChild(container);
      
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      doc.save(`Лист_согласования_${process.id}.pdf`);
    } catch (err) {
      console.error(err);
      message.error('Ошибка при формировании PDF');
    }
  };

  const handleCreateTemplate = async (values) => {
    if (stages.some(s => s.length === 0)) {
      message.error('Все этапы должны содержать хотя бы одного сотрудника');
      return;
    }
    try {
      await BPMService.createTemplate({
        name: values.name,
        description: values.description,
        steps_json: JSON.stringify(stages)
      });
      message.success('Шаблон успешно создан');
      setConstructorVisible(false);
      form.resetFields();
      setStages([[]]);
      fetchAll();
    } catch (err) {
      message.error('Ошибка при создании шаблона');
    }
  };

  const addStage = () => setStages([...stages, []]);
  const removeStage = (idx) => setStages(stages.filter((_, i) => i !== idx));
  const updateStage = (idx, value) => {
    const newStages = [...stages];
    newStages[idx] = value;
    setStages(newStages);
  };

  const statusColors = { active: 'processing', completed: 'success', cancelled: 'error' };

  const instanceColumns = [
    { title: 'ID Процесса', dataIndex: 'id', key: 'id' },
    { 
      title: 'Документ / Задача', 
      key: 'title', 
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => { setCurrentProcess(r); setDetailModalOpen(true); }} style={{ fontWeight: 'bold' }}>
            {r.title}
          </a>
          <Text type="secondary">{r.target_file_name}</Text>
        </Space>
      ) 
    },
    { title: 'Этап', dataIndex: 'current_step', key: 'step', render: (s) => <Tag color="purple">Этап {s + 1}</Tag> },
    { title: 'Статус', dataIndex: 'status', key: 'status', render: (s) => <Tag color={statusColors[s]}>{s}</Tag> },
    { title: 'Запущен', dataIndex: 'created_at', key: 'date', render: (d) => dayjs(d).format('DD.MM.YYYY HH:mm') },
    { 
      title: 'Действия', 
      key: 'actions', 
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<FilePdfOutlined />} onClick={() => handleDownloadPdf(r)}>
            Скачать PDF
          </Button>
          {(user?.role_level >= 5 || user?.username === 'khaminov') && (
            <Popconfirm
              title="Удалить процесс?"
              description="Это удалит сам процесс, все этапы согласования, а также системные уведомления во всех связанных чатах и задачах. Вы уверены?"
              onConfirm={() => handleDeleteProcess(r.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ) 
    },
  ];

  const templateColumns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Описание', dataIndex: 'description', key: 'desc' },
    { title: 'Этапов', key: 'stages', render: (_, r) => JSON.parse(r.steps_json).length },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Бизнес-процессы</h1>
        <p className="page-subtitle">Управление процессами и согласованиями</p>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 12 }}>
        <Button 
          type="primary" 
          icon={<BuildOutlined />} 
          onClick={() => {
            setLandAnalysisVisible(true);
            landForm.resetFields();
            setFileList([]);
          }} 
          style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
          }}
        >
          Анализ ЗУ
        </Button>
        {(user?.role_level >= 5 || user?.username === 'khaminov') && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setConstructorVisible(true)}
            style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
            }}
          >
            Создать шаблон
          </Button>
        )}
      </div>

      <Tabs defaultActiveKey="my" items={[
        {
          key: 'my', label: 'Мои запущенные процессы',
          children: <Table dataSource={instances} columns={instanceColumns} rowKey="id" />,
        },
        {
          key: 'templates', label: 'Справочник шаблонов',
          children: <Table dataSource={templates} columns={templateColumns} rowKey="id" />,
        },
        {
          key: 'verify', label: 'Проверка ЭЦП',
          children: (
            <Card title="Верификация подписи согласования">
              <Space direction="vertical" style={{ width: '100%', maxWidth: 600 }}>
                <Text>Введите уникальный идентификатор (Хэш) подписи с листа согласования:</Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input 
                    value={verifyHash} 
                    onChange={e => setVerifyHash(e.target.value)} 
                    placeholder="Например: a1b2c3d4e5f6..." 
                    onPressEnter={handleVerify}
                  />
                  <Button type="primary" onClick={handleVerify}>Проверить</Button>
                </Space.Compact>
                
                {verifyError && <Alert type="error" message={verifyError} showIcon />}
                
                {verifyResult && (
                  <Alert 
                    type="success" 
                    message="Подлинная подпись подтверждена!" 
                    description={
                      <Descriptions column={1} size="small" style={{ marginTop: 12 }}>
                        <Descriptions.Item label="Сотрудник"><Text strong>{verifyResult.approver_name}</Text></Descriptions.Item>
                        <Descriptions.Item label="Решение">{verifyResult.status === 'approved' ? <Tag color="success">Согласовано</Tag> : <Tag color="error">Отклонено</Tag>}</Descriptions.Item>
                        <Descriptions.Item label="Документ"><Text strong>{verifyResult.target_file_name}</Text></Descriptions.Item>
                        <Descriptions.Item label="Связанная задача">{verifyResult.task_title}</Descriptions.Item>
                        <Descriptions.Item label="Дата подписания">{dayjs(verifyResult.decided_at).format('DD.MM.YYYY HH:mm:ss')}</Descriptions.Item>
                      </Descriptions>
                    }
                    showIcon 
                  />
                )}
              </Space>
            </Card>
          ),
        },
      ]} />

      <Modal
        title="Конструктор маршрута согласования"
        open={constructorVisible}
        onCancel={() => setConstructorVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateTemplate}>
          <Form.Item name="name" label="Название шаблона" rules={[{ required: true }]}>
            <Input placeholder="Например: Согласование договора подряда" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Card title="Этапы согласования" size="small" className="bg-gray-50">
            {stages.map((stage, idx) => (
              <div key={idx} style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Tag color="purple" style={{ marginTop: 5 }}>Этап {idx + 1}</Tag>
                <div style={{ flex: 1 }}>
                  <Select
                    mode="multiple"
                    placeholder="Выберите одного или нескольких согласующих (параллельно)"
                    value={stage}
                    onChange={(val) => updateStage(idx, val)}
                    style={{ width: '100%' }}
                      showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ label: u.full_name, value: u.id }))}
                    />
                </div>
                <Button danger icon={<DeleteOutlined />} onClick={() => removeStage(idx)} disabled={stages.length === 1} />
              </div>
            ))}
            <Button type="dashed" block icon={<PlusOutlined />} onClick={addStage}>
              Добавить следующий этап
            </Button>
          </Card>

          <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setConstructorVisible(false)}>Отмена</Button>
              <Button type="primary" htmlType="submit">Сохранить шаблон</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Анализ Земельного Участка"
        open={landAnalysisVisible}
        onCancel={() => setLandAnalysisVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={landForm} layout="vertical" onFinish={handleLandAnalysisGenerate}>
          <Form.Item name="cadastral_numbers" label="Кадастровые номера (введите и нажмите Enter)" rules={[{ required: true, message: 'Обязательное поле' }]}>
            <Select mode="tags" placeholder="Например: 77:01:0001001:1234" tokenSeparators={[',']} />
          </Form.Item>
          
          <Form.Item name="address_fallback" label="Примерный адрес (Обязательно для карты, если Росреестр недоступен)">
            <Input placeholder="Например: г. Екатеринбург, ул. Ленина, д. 1" />
          </Form.Item>

          <Form.Item name="territorial_zone" label="Территориальная зона (ПЗЗ)">
            <Input placeholder="Например: Ж-5, Ц-2 (вводится вручную с Геопортала)" />
          </Form.Item>
          
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} placeholder="Дополнительная информация..." />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item name="calculation_method" label="Метод расчета" rules={[{ required: true, message: 'Обязательно' }]} style={{ flex: 1 }}>
              <Select placeholder="Выберите метод">
                <Select.Option value="Деньги">Деньги</Select.Option>
                <Select.Option value="м²">м²</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="cost" label="Стоимость" rules={[{ required: true, message: 'Обязательно' }]} style={{ flex: 1 }}>
              <Input type="number" placeholder="Введите сумму" />
            </Form.Item>

            <Form.Item name="cost_type" label="Тип стоимости" rules={[{ required: true, message: 'Обязательно' }]} style={{ flex: 1 }}>
              <Select placeholder="Выберите тип">
                <Select.Option value="Рубли">Рубли</Select.Option>
                <Select.Option value="Руб/м²">Руб/м²</Select.Option>
                <Select.Option value="% от м²">% от м²</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item label="Дополнительные файлы (необязательно)">
            <Upload.Dragger {...landUploadProps} multiple>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Нажмите или перетащите файлы для массовой загрузки</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setLandAnalysisVisible(false)}>Отмена</Button>
              <Button type="primary" htmlType="submit" loading={isGenerating}>Запустить процесс</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <ProcessDetailModal 
        process={currentProcess} 
        open={detailModalOpen} 
        onCancel={() => {
          setDetailModalOpen(false);
          setCurrentProcess(null);
          fetchAll(); // Refresh processes just in case
        }} 
        users={users}
      />
    </div>
  );
}
